import type { BreakdownRow } from '@/state/program/program-summary';
import { MIN_READABLE } from '@/state/program/program-summary';
import { cn } from '@/lib/utils';
import { formatLift, formatRate } from './outcome-tones';
import { EmptyState, GRID_TABLE_ROW, GRID_TABLE_ROWGROUP, GridTable, Meter, Panel, StackedRow } from '../ConsolePanel';

export type BreakdownTableProps = {
  title: string;
  description: string;
  rows: BreakdownRow[];
};

const TEMPLATE = 'minmax(200px,1fr) 72px 64px 64px 120px 96px';

type HeadProps = { children: string; className?: string };

const Head = ({ children, className }: HeadProps) => (
  <div role="columnheader" className={cn('micro-label', className)}>
    {children}
  </div>
);

/**
 * Win rate per group, for mechanisms and for PXL bands alike.
 *
 * A group with fewer than MIN_READABLE significant results keeps its numbers but is drawn
 * dim: hiding it would hide how thin the evidence is, and showing it at full weight
 * invites comparing a 1-of-1 with a 12-of-40.
 */
export const BreakdownTable = ({ title, description, rows }: BreakdownTableProps) => (
  <Panel title={title} description={description} bodyClassName="p-0 md:py-1">
    {!rows.length ? (
      <div className="p-3.5">
        <EmptyState>No concluded test with a significant result in this window.</EmptyState>
      </div>
    ) : (
      <>
        <div className="grid gap-2 p-3.5 md:hidden">
          {rows.map((row) => (
            <StackedRow
              key={row.key}
              className={row.decided < MIN_READABLE ? 'opacity-60' : undefined}
              heading={<span className="text-foreground text-body">{row.label}</span>}
              fields={[
                { key: 'decided', label: 'Significant', value: row.decided },
                { key: 'won', label: 'Won', value: row.won, className: 'text-ok' },
                { key: 'lost', label: 'Lost', value: row.lost, className: 'text-danger' },
                { key: 'rate', label: 'Win rate', value: formatRate(row.winRate) },
                { key: 'lift', label: 'Median win lift', value: formatLift(row.medianWinLift) },
              ]}
            />
          ))}
        </div>

        <GridTable label={title} minWidth={680} template={TEMPLATE}>
          <div role="row" className={cn(GRID_TABLE_ROW, 'border-line items-center border-b py-2')}>
            <Head>Group</Head>
            <Head className="text-right">Significant</Head>
            <Head className="text-right">Won</Head>
            <Head className="text-right">Lost</Head>
            <Head>Win rate</Head>
            <Head className="text-right">Win lift</Head>
          </div>
          <div role="rowgroup" className={GRID_TABLE_ROWGROUP}>
            {rows.map((row) => (
              <div
                key={row.key}
                role="row"
                title={
                  row.decided < MIN_READABLE
                    ? `Only ${row.decided} with a significant result — too few to compare`
                    : undefined
                }
                className={cn(
                  GRID_TABLE_ROW,
                  'border-line items-center border-b py-2 last:border-b-0',
                  row.decided < MIN_READABLE && 'opacity-60',
                )}
              >
                <div role="cell" className="text-foreground truncate text-body">
                  {row.label}
                </div>
                <div role="cell" className="num text-foreground text-right">
                  {row.decided}
                </div>
                <div role="cell" className="num text-ok text-right">
                  {row.won}
                </div>
                <div role="cell" className="num text-danger text-right">
                  {row.lost}
                </div>
                <div role="cell" className="flex items-center gap-2">
                  <span className="num text-foreground w-9 shrink-0">{formatRate(row.winRate)}</span>
                  <Meter percent={row.winRate ?? 0} className="bg-ok" />
                </div>
                <div role="cell" className="num text-foreground text-right">
                  {formatLift(row.medianWinLift)}
                </div>
              </div>
            ))}
          </div>
        </GridTable>
      </>
    )}
  </Panel>
);
